
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Country } from '@/contexts/country/types';
import { useIsMobile } from '@/hooks/use-mobile';
import { Search, Filter } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ConstituencyFiltersProps {
  selectedCountry: string;
  setSelectedCountry: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  availableCountries: Country[];
  activeFiltersCount: number;
  onClearFilters: () => void;
}

const ConstituencyFilters: React.FC<ConstituencyFiltersProps> = ({
  selectedCountry,
  setSelectedCountry,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  availableCountries,
  activeFiltersCount,
  onClearFilters
}) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-4 flex-1">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search constituencies..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="w-full sm:w-auto"
          >
            Clear filters
          </Button>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        <div className="text-nation-blue dark:text-blue-300 flex items-center mr-2">
          <Filter size={18} className="mr-1" />
          <span>Filter:</span>
        </div>
        
        <Select 
          value={selectedCountry} 
          onValueChange={(value) => setSelectedCountry(value)}
        >
          <SelectTrigger className={isMobile ? "w-full" : "w-[200px]"}>
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {availableCountries.map(country => (
              <SelectItem key={country.code} value={country.code}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select 
          value={statusFilter} 
          onValueChange={(value) => setStatusFilter(value)}
        >
          <SelectTrigger className={isMobile ? "w-full" : "w-[140px]"}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Active Filters Badge */}
        {activeFiltersCount > 0 && (
          <Badge variant="secondary" className="h-9 px-3 flex items-center">
            {activeFiltersCount} {activeFiltersCount === 1 ? 'filter' : 'filters'} active
          </Badge>
        )}
      </div>
    </div>
  );
};

export default ConstituencyFilters;
